import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { get } from 'lodash';
import { Upload } from 'lucide-react';

import { getStatementsAction, processStatementAction } from 'actions/bankStatements';
import { backAction, nextAction } from 'actions/workflow';
import { useConfig } from 'config/ConfigContext';
import useWorkflowGuard from 'hooks/useWorkflowGuard';
import UploadGuidelines from 'components/UploadGuidelines';
import StatementsTable from 'components/StatementsTable';
import ProgressLoading from 'components/ProgressLoading';
import { FormRow } from 'components/Form';
import { useToast } from 'components/Toast';
import { Button } from 'components/Button';
import Spinner from 'components/Spinner';
import Modal from 'components/Modal';
import GeneralError from 'components/GeneralError';
import { MAXIMUM_FILE_SIZE_MB, PROGRESS_BAR_DURATION_MS } from 'utils/constants';
import { hideTitleAndFooter, showTitleAndFooter } from 'utils/helper';

let bankWebSocket = null;
const NORMAL_CLOSE = 'Normal close';

const ManualVerification = ({
  navigate,
  dispatch,
  searchParams,
}) => {
  useWorkflowGuard(searchParams, navigate);
  const { showToast } = useToast();
  const { websocketUrl } = useConfig();
  const statements = useSelector((state) => get(state, 'bankStatements.statements') || []);
  const workflow = useSelector((state) => get(state, 'workflow'))
  const [isDragging, setIsDragging] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingBack, setIsLoadingBack] = useState(false);
  const [generalError, setGeneralError] = useState(null);
  const [progress, setProgress] = useState(0);
  const [showUnmatchedConditionsPopup, setShowUnmatchedConditionsPopup] = useState(false);
  const applicationId = get(workflow, 'attributes.entityId');
  const bankProcessorKey = get(workflow, 'data.bankProcessorKey');

  const loadStatements = () => {
    if (!applicationId) return;

    setIsLoading(true);

    dispatch(getStatementsAction({
      entityId: applicationId,
      entityType: 'application',
      bankProcessorKey,
      success: () => {
        setIsLoading(false);
      },
      fail: () => {
        setIsLoading(false);
        showToast('Failed to load statements', 'error');
      },
    }));
  };

  const listenBankStatementWebSocket = () => {
    bankWebSocket = new WebSocket(`${websocketUrl}?application_id=${applicationId}`);
    console.log('connection:', bankWebSocket);
    bankWebSocket.addEventListener('message', (event) => {
      console.log('eventData:', event.data);
      const eventData = JSON.parse(get(event, 'data'));
      const eventType = get(eventData, 'EventType');
      if (eventType === 'BANK_STATEMENT_PROCESSING_COMPLETE') {
        loadStatements();
      }
    });
    bankWebSocket.addEventListener('close', (event) => {
      if (get(event, 'reason') !== NORMAL_CLOSE || get(event, 'code') !== 1000) {
        console.log('Closed and Reconnecting:', event);
        listenBankStatementWebSocket();
      }
    });
  };

  useEffect(() => {
    if (applicationId) {
      loadStatements();
      listenBankStatementWebSocket();
    }

    return () => {
      bankWebSocket && bankWebSocket.close(1000, NORMAL_CLOSE);
      bankWebSocket = null;
    };
  }, [applicationId]);

  const handleLinkMore = () => {
    setIsLoadingBack(true);
    dispatch(backAction({
      key: searchParams.get('key'),
      data: {},
      success: (res) => {
        setIsLoadingBack(false);
        const nextUrl = get(res, 'data.url')
        if (nextUrl) navigate(nextUrl);
      },
      fail: handleFail,
    }));
  };

  const processStatement = async (file) => {
    if (!applicationId) {
      showToast('Application ID is required', 'error');
      return;
    }

    setIsProcessing(true);
    console.log(`Processing statement: ${file.name} for application ID: ${applicationId}`);

    const formData = new FormData();
    formData.append('file', file);

    dispatch(processStatementAction({
      entityType: 'application',
      entityId: applicationId,
      data: formData,
      bankProcessorKey,
      success: async (res) => {
        if (!res.success) {
          showToast(res.error.message, 'error');
          setIsProcessing(false);
          return;
        }
        showToast('Statement Received', 'success');
        loadStatements();
        setIsProcessing(false);
      },
      fail: (error) => {
        setIsProcessing(false);
        console.error('Processing error:', error);
        if (error instanceof Error) {
          showToast(error.message || 'Failed to process statement', 'error');
        } else {
          showToast(
            'An unexpected error occurred while processing the statement',
            'error'
          );
        }
      },
    }));
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    setIsDragging(false);
    const { 0: file } = e.dataTransfer.files;
    if (file && file.type === 'application/pdf') {
      if (file.size > MAXIMUM_FILE_SIZE_MB * 1024 * 1024) {
        showToast('File cannot be processed. Maximum file size is 10MB.', 'error');
        return;
      }
      await processStatement(file);
    } else {
      showToast('Please upload a PDF file.', 'error');
    }
  };

  const handleFileInput = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (file) {
      console.log('File selected:', file.name, file.size, file.type);
      if (file.size > MAXIMUM_FILE_SIZE_MB * 1024 * 1024) {
        showToast('File cannot be processed. Maximum file size is 10MB.', 'error');
        return;
      }
      await processStatement(file);
    }
  };

  const handleFail = (err) => {
    const errorMessage = get(err, 'data.failure.errors.0.message');
    const errorSubject = get(err, 'data.failure.errors.0.subject');
    if (errorSubject === 'bankStatement') {
      setShowUnmatchedConditionsPopup(true);
    } else if (errorMessage) {
      setGeneralError(get(err, 'data.failure.errors.0'));
    }
    setIsSubmitting(false);
    showTitleAndFooter();
  }

  const handleNext = () => {
    setIsSubmitting(true);
    hideTitleAndFooter();
    setProgress(0);
    
    const interval = setInterval(() => {
      setProgress(prev => prev < 90 ? prev + (90 / (PROGRESS_BAR_DURATION_MS / 100)) : 90);
    }, 100);

    dispatch(nextAction({
      key: searchParams.get('key'),
      data: {},
      success: (res) => {
        clearInterval(interval);
        setProgress(100);
        setTimeout(() => {
          setIsSubmitting(false);
          const nextUrl = get(res, 'data.url')
          if (nextUrl) navigate(nextUrl);
          showTitleAndFooter();
        }, 1000);
      },
      fail: (err) => {
        clearInterval(interval);
        handleFail(err);
      },
    }));
  };

  if (isSubmitting) {
    return <ProgressLoading className="mb-8" progress={progress}/>
  }

  return (
    <div className="bank-statement-container">
      <div className="relative mb-4 min-h-12">
        <div className="absolute top-0 right-0 z-10 max-w-full">
          <UploadGuidelines />
        </div>
      </div>
      <div
        className={`w-full relative border border-gray-200 rounded-lg p-12 flex flex-col items-center justify-center ${
          isDragging ? "border-blue-500 bg-blue-50" : ""
        } hover:border-blue-500 hover:bg-blue-50 transition-colors duration-200 mb-4`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {isProcessing ? (
          <div style={{ textAlign: 'center' }}>
            <Spinner size={6} color="blue-500" />
            <p className="text-slate-600 mt-3">
              Processing statement...
            </p>
          </div>
        ) : (
          <>
            <p className="text-gray-600 mb-6 text-center text-sm md:text-base">
              Drag and drop your PDF statement here
            </p>
            <div className="flex items-center justify-center">
              <Button
                className="button !text-sm !px-4 !py-2 !font-normal shadow-md"
                label="Choose File"
                icon={Upload}
                iconGap={2}
              />
              <input
                type="file"
                accept="application/pdf"
                onChange={handleFileInput}
                disabled={isProcessing}
                style={{
                  position: 'absolute',
                  top: 0,
                  margin: 0,
                  left: 0,
                  opacity: 0,
                  width: '100%',
                  height: '100%',
                  cursor: 'pointer',
                }}
              />
            </div>
          </>
        )}
      </div>

      <div className="mb-8">
        <StatementsTable
          statements={statements}
          isLoading={isLoading}
        />
      </div>

      {showUnmatchedConditionsPopup && (
        <Modal
          title="We need a bit more information"
          onClose={() => setShowUnmatchedConditionsPopup(false)}
        >
          <p className="text-sm text-gray-600 mb-6">
            We weren't able to retrieve enough account details to continue. To keep your application moving, please upload your bank statements for review.
          </p>
          <Button
            className="w-full text-sm !py-3"
            label="Upload Bank Statements"
            modifier="primary"
            onClick={() => setShowUnmatchedConditionsPopup(false)}
          />
        </Modal>
      )}

      <GeneralError error={generalError} />

      <FormRow className="flex-col-reverse">
        <Button
          className="flex-1"
          modifier="outline"
          label="+ Link More"
          onClick={handleLinkMore}
          isLoading={isLoadingBack}
        />
        <Button
          className="flex-1"
          label="Next Step: Offer Selection"
          isLoading={isSubmitting}
          isDisabled={isLoading || !statements || statements.filter((s) => !s.isDeleted).length === 0 || statements.filter((acc) => acc.processingStatus === 'InProgress').length > 0}
          onClick={handleNext}
        />
      </FormRow>
    </div>
  );
};

ManualVerification.propTypes = {
  navigate: PropTypes.func.isRequired,
  dispatch: PropTypes.func.isRequired,
  searchParams: PropTypes.object.isRequired
};

export default ManualVerification;